import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { AdminBonusPuzzle } from "@/components/AdminBonusPuzzle";
import { AdminNewJackBatches } from "@/components/AdminNewJackBatches";
import { AdminEditorialReview } from "@/components/AdminEditorialReview";

const TABS = ["stats", "puzzles", "create", "users", "reports", "submissions", "wordbank", "daily", "settings"];

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [data, setData] = useState({});

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/"); }
  }, [user, loading, navigate]);

  const load = useCallback(async (t) => {
    try {
      if (t === "stats") { const r = (await api.get("/admin/stats")).data; setData((d) => ({ ...d, stats: r })); }
      if (t === "puzzles") { const r = (await api.get("/admin/puzzles")).data.puzzles; setData((d) => ({ ...d, puzzles: r })); }
      if (t === "users") { const r = (await api.get("/admin/users")).data.users; setData((d) => ({ ...d, users: r })); }
      if (t === "reports") { const r = (await api.get("/admin/reports")).data.reports; setData((d) => ({ ...d, reports: r })); }
      if (t === "submissions") { const r = (await api.get("/admin/submissions")).data.submissions; setData((d) => ({ ...d, submissions: r })); }
      if (t === "wordbank") { const r = (await api.get("/admin/wordbank")).data.entries; setData((d) => ({ ...d, wordbank: r })); }
      if (t === "daily") { const dl = (await api.get("/admin/daily")).data.daily; const pz = (await api.get("/admin/puzzles")).data.puzzles; setData((d) => ({ ...d, daily: dl, puzzles: pz })); }
      if (t === "settings") { const r = (await api.get("/admin/settings")).data; setData((d) => ({ ...d, settings: r })); }
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { if (user?.role === "admin") load(tab); }, [tab, user, load]);

  if (!user || user.role !== "admin") return <Layout><div className="py-32 text-center text-muted-foreground">Restricted.</div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <p className="font-mono-caps text-xs text-secondary mb-2">Admin Control</p>
        <h1 className="font-display text-5xl mb-8">DASHBOARD</h1>
        <AdminEditorialReview />
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`font-mono-caps text-[10px] px-4 py-2 border transition-colors ${tab === t ? "bg-secondary text-secondary-foreground border-secondary" : "border-white/20 text-muted-foreground hover:border-secondary"}`}>{t === "wordbank" ? "Word Generator" : t}</button>
          ))}
        </div>

        {tab === "stats" && <Stats stats={data.stats} />}
        {tab === "puzzles" && <><AdminBonusPuzzle puzzles={data.puzzles} /><Puzzles rows={data.puzzles} reload={() => load("puzzles")} /></>}
        {tab === "create" && <CreatePuzzle reload={() => { setTab("puzzles"); }} />}
        {tab === "users" && <Users rows={data.users} reload={() => load("users")} />}
        {tab === "reports" && <Reports rows={data.reports} reload={() => load("reports")} />}
        {tab === "submissions" && <Submissions rows={data.submissions} reload={() => load("submissions")} />}
        {tab === "wordbank" && <WordBank rows={data.wordbank} reload={() => load("wordbank")} />}
        {tab === "daily" && <Daily rows={data.daily} puzzles={data.puzzles} reload={() => load("daily")} />}
        {tab === "settings" && <Settings s={data.settings} reload={() => load("settings")} />}
      </div>
    </Layout>
  );
}

const Cell = ({ children, className = "" }) => <td className={`px-4 py-3 border-b border-white/5 ${className}`}>{children}</td>;
const Th = ({ children }) => <th className="px-4 py-3 text-left font-mono-caps text-[9px] text-muted-foreground border-b border-white/10">{children}</th>;

function Stats({ stats }) {
  if (!stats) return <p className="text-muted-foreground text-sm">Loading…</p>;
  const items = [
    ["Users", stats.users], ["Premium", stats.premium_users], ["Puzzles", stats.puzzles],
    ["Reports Open", stats.reports_open], ["Pending Subs", stats.submissions_pending],
    ["Games Played", stats.games_played], ["Solved", stats.puzzles_solved],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="admin-stats">
      {items.map(([l, v]) => (
        <div key={l} className="border border-white/15 bg-card p-5">
          <div className="font-display text-4xl text-primary">{v ?? 0}</div>
          <div className="font-mono-caps text-[9px] text-muted-foreground mt-1">{l}</div>
        </div>
      ))}
    </div>
  );
}

function Puzzles({ rows, reload }) {
  const [editing, setEditing] = useState(null);
  const del = async (id) => { if (!window.confirm("Delete this puzzle?")) return; await api.delete(`/admin/puzzles/${id}`); toast.success("Deleted"); reload(); };
  return (
    <div className="border border-white/15 bg-card overflow-x-auto no-scrollbar" data-testid="admin-puzzles">
      <table className="w-full text-sm"><thead><tr><Th>Title</Th><Th>Zone</Th><Th>Words</Th><Th>Status</Th><Th>Used</Th><Th></Th></tr></thead>
        <tbody>{(rows || []).map((p) => (
          <tr key={p.puzzle_id}>
            <Cell>{p.title}{p.bonus_only && <span className="font-mono-caps text-[8px] text-primary ml-2">BONUS VAULT</span>}{p.edit_history?.length ? <span className="font-mono-caps text-[8px] text-secondary ml-2">edited ×{p.edit_history.length}</span> : null}</Cell>
            <Cell className="text-muted-foreground">{p.zone}</Cell><Cell>{p.word_count}</Cell>
            <Cell><span className="font-mono-caps text-[9px] text-secondary">{p.status}</span></Cell><Cell>{p.times_used}</Cell>
            <Cell className="whitespace-nowrap">
              <button onClick={() => setEditing(p.puzzle_id)} data-testid={`edit-puzzle-${p.puzzle_id}`} className="text-primary font-mono-caps text-[9px] hover:underline mr-3">Edit</button>
              <button onClick={() => del(p.puzzle_id)} data-testid={`del-puzzle-${p.puzzle_id}`} className="text-destructive font-mono-caps text-[9px] hover:underline">Delete</button>
            </Cell>
          </tr>))}</tbody>
      </table>
      {editing && <PuzzleEditModal puzzleId={editing} onClose={() => setEditing(null)} reload={reload} />}
    </div>
  );
}

function PuzzleEditModal({ puzzleId, onClose, reload }) {
  const [meta, setMeta] = useState(null);
  const [words, setWords] = useState([]);
  const [initial, setInitial] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/puzzles/${puzzleId}`).then(({ data }) => {
      const w = [...data.across, ...data.down].map((c) => ({ answer: c.answer, clue: c.clue }));
      setMeta({ title: data.title, zone: data.zone, difficulty: data.difficulty || "medium", source: data.source || "" });
      setWords(w);
      setInitial(JSON.stringify({ w, m: { title: data.title, difficulty: data.difficulty, source: data.source } }));
    }).catch((e) => toast.error(apiError(e)));
  }, [puzzleId]);

  const dirty = meta && JSON.stringify({ w: words, m: { title: meta.title, difficulty: meta.difficulty, source: meta.source } }) !== initial;
  const setWord = (i, field, v) => setWords((ws) => ws.map((w, idx) => (idx === i ? { ...w, [field]: v } : w)));
  const removeWord = (i) => setWords((ws) => ws.filter((_, idx) => idx !== i));
  const addWord = () => setWords((ws) => [...ws, { answer: "", clue: "" }]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/puzzles/${puzzleId}/edit`, { ...meta, words });
      toast.success(data.rebuilt ? `Grid rebuilt & validated · ${data.word_count} words` : `Clues updated · grid preserved`);
      onClose(); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!meta) return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80"><p className="font-mono-caps text-xs text-primary animate-pulse">Loading puzzle…</p></div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="puzzle-edit-modal">
      <div className="w-full max-w-2xl bg-card border-2 border-primary p-6 fade-up max-h-[92vh] overflow-auto no-scrollbar">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl">Edit Puzzle</h3>
          {dirty && <span data-testid="unsaved-badge" className="font-mono-caps text-[9px] text-amber-500 border border-amber-500/50 px-2 py-1">Unsaved changes</span>}
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="col-span-2"><label className="font-mono-caps text-[9px] text-muted-foreground">Title</label>
            <input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} data-testid="edit-title" className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm" /></div>
          <div><label className="font-mono-caps text-[9px] text-muted-foreground">Difficulty</label>
            <select value={meta.difficulty} onChange={(e) => setMeta({ ...meta, difficulty: e.target.value })} className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm"><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option></select></div>
        </div>
        <p className="font-mono-caps text-[9px] text-muted-foreground mb-2">Words ({words.length}) — editing answers rebuilds & revalidates the 18×18 grid</p>
        <div className="space-y-2 mb-4">
          {words.map((w, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`edit-word-${i}`}>
              <input value={w.answer} onChange={(e) => setWord(i, "answer", e.target.value)} placeholder="ANSWER" className="w-32 bg-background border border-border px-2 py-1.5 text-sm uppercase font-mono" />
              <input value={w.clue} onChange={(e) => setWord(i, "clue", e.target.value)} placeholder="Clue" className="flex-1 bg-background border border-border px-2 py-1.5 text-sm" />
              <button onClick={() => removeWord(i)} data-testid={`remove-word-${i}`} className="text-destructive font-mono-caps text-[9px] px-2 hover:underline">×</button>
            </div>
          ))}
        </div>
        <button onClick={addWord} data-testid="add-word-btn" className="font-mono-caps text-[9px] text-secondary hover:underline mb-5">+ Add word</button>
        <div className="flex gap-3">
          <button onClick={save} disabled={busy || !dirty} data-testid="save-puzzle-btn" className="flex-1 bg-primary text-primary-foreground font-mono-caps text-xs py-3 border border-primary hover-lift disabled:opacity-40">{busy ? "Validating…" : "Validate & Save"}</button>
          <button onClick={onClose} className="border border-white/20 font-mono-caps text-xs px-5 hover:border-white/40">Close</button>
        </div>
      </div>
    </div>
  );
}

function CreatePuzzle({ reload }) {
  const [bonusOnly, setBonusOnly] = useState(false);
  const [zone, setZone] = useState("casual");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("NAS: Illmatic emcee from Queensbridge\nBEAT: The rhythmic instrumental\nMIC: What you grab to rhyme\nFLOW: How an MC rides the rhythm");
  const create = async () => {
    const words = text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const i = l.indexOf(":"); return i < 0 ? null : { answer: l.slice(0, i).trim(), clue: l.slice(i + 1).trim() };
    }).filter(Boolean);
    try { const { data } = await api.post("/admin/puzzles", { zone, title, difficulty: "medium", source: "Admin", words, bonus_only: bonusOnly });
      toast.success(`Created with ${data.word_count} words`); reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="border border-white/15 bg-card p-6 max-w-2xl space-y-4" data-testid="admin-create-puzzle">
      <p className="text-sm text-muted-foreground">Enter one <span className="text-primary">ANSWER: clue</span> per line (min 4). The grid auto-generates with interlocking words.</p>
      <div className="flex gap-3">
        <select value={zone} onChange={(e) => setZone(e.target.value)} data-testid="create-zone" className="bg-background border border-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary">
          <option value="casual">New Jack</option><option value="golden_era">Triple OG</option>
        </select>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="create-title" className="flex-1 bg-background border border-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={bonusOnly} onChange={(e) => setBonusOnly(e.target.checked)} data-testid="create-bonus-only" /> Bonus-Only (Bonus Vault) — earned access only</label>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="create-words" className="w-full bg-background border border-border px-3 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-primary resize-none" />
      <button onClick={create} data-testid="create-puzzle-btn" className="bg-secondary text-secondary-foreground font-mono-caps text-xs px-6 py-3 border border-secondary hover-lift">Generate Puzzle</button>
    </div>
  );
}

function Users({ rows, reload }) {
  const toggle = async (u, field) => {
    const body = field === "premium" ? { is_premium: !u.is_premium } : { role: u.role === "admin" ? "user" : "admin" };
    await api.put(`/admin/users/${u.user_id}`, body); toast.success("Updated"); reload();
  };
  return (
    <div className="border border-white/15 bg-card overflow-x-auto no-scrollbar" data-testid="admin-users">
      <table className="w-full text-sm"><thead><tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Premium</Th></tr></thead>
        <tbody>{(rows || []).map((u) => (
          <tr key={u.user_id}>
            <Cell>{u.name}</Cell><Cell className="text-muted-foreground">{u.email}</Cell>
            <Cell><button onClick={() => toggle(u, "role")} className="font-mono-caps text-[9px] text-secondary hover:underline">{u.role}</button></Cell>
            <Cell><button onClick={() => toggle(u, "premium")} data-testid={`toggle-premium-${u.user_id}`} className={`font-mono-caps text-[9px] hover:underline ${u.is_premium ? "text-primary" : "text-muted-foreground"}`}>{u.is_premium ? "yes" : "no"}</button></Cell>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function Reports({ rows, reload }) {
  const set = async (id, status) => { await api.put(`/admin/reports/${id}`, { status }); reload(); };
  return (
    <div className="border border-white/15 bg-card overflow-x-auto no-scrollbar" data-testid="admin-reports">
      <table className="w-full text-sm"><thead><tr><Th>Type</Th><Th>Message</Th><Th>By</Th><Th>Status</Th><Th></Th></tr></thead>
        <tbody>{(rows || []).length === 0 ? <tr><Cell className="text-muted-foreground">No reports.</Cell></tr> : rows.map((r) => (
          <tr key={r.report_id}>
            <Cell><span className="font-mono-caps text-[9px] text-primary">{r.type}</span></Cell><Cell className="text-muted-foreground max-w-xs truncate">{r.message}</Cell>
            <Cell>{r.user_name}</Cell><Cell>{r.status}</Cell>
            <Cell className="whitespace-nowrap">
              <button onClick={() => set(r.report_id, "resolved")} className="font-mono-caps text-[9px] text-secondary hover:underline mr-3">resolve</button>
              <button onClick={() => set(r.report_id, "dismissed")} className="font-mono-caps text-[9px] text-muted-foreground hover:underline">dismiss</button>
            </Cell>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function Submissions({ rows, reload }) {
  const [review, setReview] = useState(null);
  const setStatus = async (id, status) => { await api.put(`/admin/submissions/${id}`, { status }); toast.success(status); reload(); };
  const questions = (rows || []).filter((s) => s.type === "question");
  const shoutouts = (rows || []).filter((s) => s.type !== "question");
  return (
    <div className="space-y-8" data-testid="admin-submissions">
      <div>
        <p className="font-mono-caps text-[10px] text-primary mb-3">Question Submissions → Word Bank</p>
        <div className="border border-white/15 bg-card overflow-x-auto no-scrollbar">
          <table className="w-full text-sm"><thead><tr><Th>Zone</Th><Th>Answer</Th><Th>Clue</Th><Th>By</Th><Th>Status</Th><Th></Th></tr></thead>
            <tbody>{questions.length === 0 ? <tr><Cell className="text-muted-foreground">No question submissions.</Cell></tr> : questions.map((s) => (
              <tr key={s.submission_id}>
                <Cell className="text-muted-foreground">{s.zone}</Cell><Cell className="font-mono uppercase">{s.answer}</Cell>
                <Cell className="max-w-xs truncate">{s.clue}</Cell><Cell>{s.user_name}</Cell>
                <Cell><span className={`font-mono-caps text-[9px] ${s.status === "approved" ? "text-secondary" : s.status === "rejected" ? "text-destructive" : s.status === "duplicate" ? "text-amber-500" : "text-muted-foreground"}`}>{s.status}</span></Cell>
                <Cell><button onClick={() => setReview(s)} data-testid={`review-${s.submission_id}`} className="font-mono-caps text-[9px] text-primary hover:underline">review</button></Cell>
              </tr>))}</tbody>
          </table>
        </div>
      </div>
      <div>
        <p className="font-mono-caps text-[10px] text-secondary mb-3">Shout-Out Submissions</p>
        <div className="border border-white/15 bg-card overflow-x-auto no-scrollbar">
          <table className="w-full text-sm"><thead><tr><Th>Content</Th><Th>By</Th><Th>Status</Th><Th></Th></tr></thead>
            <tbody>{shoutouts.length === 0 ? <tr><Cell className="text-muted-foreground">No shout-outs.</Cell></tr> : shoutouts.map((s) => (
              <tr key={s.submission_id}>
                <Cell className="max-w-md truncate">{s.content}{s.auto_credit && <span className="font-mono-caps text-[8px] text-secondary ml-2">auto-credit</span>}</Cell>
                <Cell>{s.user_name}</Cell><Cell>{s.status}</Cell>
                <Cell className="whitespace-nowrap">
                  <button onClick={() => setStatus(s.submission_id, "approved")} data-testid={`approve-${s.submission_id}`} className="font-mono-caps text-[9px] text-secondary hover:underline mr-3">approve</button>
                  <button onClick={() => setStatus(s.submission_id, "rejected")} className="font-mono-caps text-[9px] text-destructive hover:underline">reject</button>
                </Cell>
              </tr>))}</tbody>
          </table>
        </div>
      </div>
      {review && <ReviewModal sub={review} onClose={() => setReview(null)} reload={reload} />}
    </div>
  );
}

function ReviewModal({ sub, onClose, reload }) {
  const [form, setForm] = useState({
    zone: sub.zone || "casual", answer: sub.answer || "", clue: sub.clue || "",
    category: sub.category || "", difficulty: sub.difficulty || "medium", source: sub.source || "Community",
  });
  const [busy, setBusy] = useState(false);
  const act = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/submissions/${sub.submission_id}`, { status, ...form });
      if (data.duplicate) toast.warning(data.message || "Duplicate — not added to word bank");
      else if (status === "approved") toast.success("Approved → added to word bank");
      else toast.success("Rejected");
      onClose(); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="review-modal">
      <div className="w-full max-w-lg bg-card border-2 border-primary p-6 fade-up max-h-[90vh] overflow-auto no-scrollbar">
        <h3 className="font-display text-2xl mb-1">Review Submission</h3>
        <p className="font-mono-caps text-[9px] text-muted-foreground mb-5">by {sub.user_name} · edit before approving</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="font-mono-caps text-[9px] text-muted-foreground">Zone</label>
              <select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} data-testid="review-zone" className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm">
                <option value="casual">New Jack</option><option value="golden_era">Triple OG</option></select></div>
            <div><label className="font-mono-caps text-[9px] text-muted-foreground">Difficulty</label>
              <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm">
                <option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option></select></div>
          </div>
          <div><label className="font-mono-caps text-[9px] text-muted-foreground">Answer (A-Z)</label>
            <input value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="review-answer" className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm uppercase font-mono" /></div>
          <div><label className="font-mono-caps text-[9px] text-muted-foreground">Clue</label>
            <textarea value={form.clue} onChange={(e) => setForm({ ...form, clue: e.target.value })} rows={2} data-testid="review-clue" className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm resize-none" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="font-mono-caps text-[9px] text-muted-foreground">Category</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. artist" className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm" /></div>
            <div><label className="font-mono-caps text-[9px] text-muted-foreground">Source / Reference</label>
              <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full mt-1 bg-background border border-border px-3 py-2 text-sm" /></div>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={() => act("approved")} disabled={busy} data-testid="review-approve" className="flex-1 bg-secondary text-secondary-foreground font-mono-caps text-xs py-3 border border-secondary hover-lift disabled:opacity-50">Approve → Word Bank</button>
          <button onClick={() => act("rejected")} disabled={busy} data-testid="review-reject" className="flex-1 border border-destructive text-destructive font-mono-caps text-xs py-3 hover:bg-destructive/10 disabled:opacity-50">Reject</button>
          <button onClick={onClose} className="border border-white/20 font-mono-caps text-xs px-4 hover:border-white/40">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function WordBank({ rows, reload }) {
  const [exporting, setExporting] = useState(false);
  const exportReferenceBank = async () => {
    setExporting(true);
    try {
      // Use this site's backend, irrespective of the shared client's build-time URL.
      const endpoint = new URL("/api/admin/reference-bank/export", window.location.origin).href;
      const { data } = await api.get(endpoint, { responseType: "blob" });
      const text = await data.text();
      const reference = JSON.parse(text);
      if (reference.schema_version !== 1 || typeof reference.exported_at !== "string" || !Array.isArray(reference.word_bank) || !Array.isArray(reference.puzzles)
        || reference.counts?.word_bank !== reference.word_bank.length || reference.counts?.puzzles !== reference.puzzles.length) {
        throw new Error("The reference export is incomplete. Please try again.");
      }
      const url = URL.createObjectURL(new Blob([text], { type: "application/json;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "hhkd-reference-bank-" + reference.exported_at.slice(0, 10) + ".json";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Exported " + reference.counts.word_bank + " Word Bank entries and " + reference.counts.puzzles + " puzzles");
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };
  const [zone, setZone] = useState("casual");
  const [origin, setOrigin] = useState("");
  const [busy, setBusy] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBatchDrafts, setShowBatchDrafts] = useState(false);
  const filtered = (rows || []).filter((e) => (!zone || e.zone === zone) && (!origin || e.origin === origin));
  const levelEntries = (rows || []).filter((e) => e.zone === zone);
  const availableCount = levelEntries.filter(e => e.status === "active" && !e.used_count).length;
  const toggle = async (e) => { await api.put(`/admin/wordbank/${e.entry_id}`, { status: e.status === "retired" ? "active" : "retired" }); reload(); };
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/puzzles/generate", { zone });
      toast.success(`Puzzle created · ${data.word_count} words · ${data.community_used} community clue(s) credited`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="admin-wordbank">
      <h2 className="font-display text-3xl">WORD GENERATOR</h2>
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs">Puzzle level
          <select value={zone} onChange={(e) => { setZone(e.target.value); setOrigin(""); }} data-testid="wb-zone" className="block mt-1 bg-background border border-border px-3 py-2 text-sm"><option value="casual">New Jack</option><option value="golden_era">Triple OG</option><option value="bonus">Bonus</option></select>
        </label>
        <select value={origin} onChange={(e) => setOrigin(e.target.value)} data-testid="wb-origin" className="bg-background border border-border px-3 py-2 text-sm"><option value="">All origins</option><option value="seed">Seed / Admin</option><option value="community">Community</option></select>
        <div className="flex-1" />
        <button type="button" onClick={exportReferenceBank} disabled={exporting} data-testid="wb-export-reference-btn" className="border border-primary text-primary font-mono-caps text-[10px] px-4 py-2.5 hover:bg-primary/10 disabled:opacity-50">{exporting ? "EXPORTING…" : "EXPORT REFERENCE BANK"}</button>
        <button onClick={() => setShowImport(true)} data-testid="wb-import-btn" className="border border-secondary text-secondary font-mono-caps text-[10px] px-4 py-2.5 hover:bg-secondary/10">Bulk Import</button>
        <button onClick={generate} disabled={busy || availableCount < 4} data-testid="wb-generate-btn" className="bg-primary text-primary-foreground font-mono-caps text-[10px] px-5 py-2.5 border border-primary hover-lift disabled:opacity-50">
          {busy ? "Generating…" : "Generate Puzzle from Word Bank"}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">{levelEntries.length} entries · {availableCount} active AVAILABLE. {zone === "bonus" && !levelEntries.length ? "Bonus is ready for its first words. Use Bulk Import to add them." : "Only active, AVAILABLE words are eligible for new puzzles."}</p>
      <div className="border border-white/15 bg-card overflow-x-auto no-scrollbar">
        <table className="w-full text-sm"><thead><tr><Th>Answer</Th><Th>Clue</Th><Th>Origin</Th><Th>Contributor</Th><Th>Eligibility</Th><Th>Status</Th><Th></Th></tr></thead>
          <tbody>{filtered.length === 0 ? <tr><Cell className="text-muted-foreground">No words in this selection yet.</Cell></tr> : filtered.map((e) => (
            <tr key={e.entry_id} className={e.status === "retired" ? "opacity-50" : ""}>
              <Cell className="font-mono uppercase">{e.answer}</Cell>
              <Cell className="max-w-xs truncate text-muted-foreground">{e.clue}</Cell>
              <Cell><span className={`font-mono-caps text-[9px] ${e.origin === "community" ? "text-secondary" : "text-muted-foreground"}`}>{e.origin === "community" ? "community" : "seed/admin"}</span></Cell>
              <Cell>{e.origin === "community" ? e.contributor_name : "—"}</Cell>
              <Cell><span data-testid={`wb-eligibility-${e.entry_id}`} className={`font-mono-caps text-[9px] px-2 py-0.5 border ${e.used_count > 0 ? "text-muted-foreground border-white/20" : "text-secondary border-secondary/50"}`}>{e.used_count > 0 ? `USED · ${e.used_count}` : "AVAILABLE"}</span></Cell>
              <Cell><span className={`font-mono-caps text-[9px] ${e.status === "active" ? "text-primary" : "text-muted-foreground"}`}>{e.status}</span></Cell>
              <Cell><button onClick={() => toggle(e)} data-testid={`wb-toggle-${e.entry_id}`} className="font-mono-caps text-[9px] hover:underline text-secondary">{e.status === "retired" ? "activate" : "retire"}</button></Cell>
            </tr>))}</tbody>
        </table>
      </div>
      {showImport && <BulkImportModal defaultZone={zone} onClose={() => setShowImport(false)} reload={reload} />}
      {zone === "casual" && <div className="border border-white/15 p-4" data-testid="new-jack-batch-details">
        <button type="button" onClick={() => setShowBatchDrafts(v => !v)} aria-expanded={showBatchDrafts} className="font-mono-caps text-sm text-left">{showBatchDrafts ? "Hide" : "Open"} New Jack batch drafts (7–30)</button>
        {showBatchDrafts && <div className="mt-5"><AdminNewJackBatches /></div>}
      </div>}
    </div>
  );
}

function BulkImportModal({ defaultZone, onClose, reload }) {
  const [form, setForm] = useState({ zone: defaultZone, category: "", difficulty: "medium", source: "Admin Import" });
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wordbank/bulk", { ...form, text });
      setResult(data);
      toast.success(`${data.summary.accepted} added · ${data.summary.flagged} flagged · ${data.summary.rejected} rejected`);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="bulk-import-modal">
      <div className="w-full max-w-2xl bg-card border-2 border-secondary p-6 fade-up max-h-[92vh] overflow-auto no-scrollbar">
        <h3 className="font-display text-2xl mb-1">Bulk Import → Word Bank</h3>
        <p className="text-sm text-muted-foreground mb-4">One entry per line in the format <span className="text-primary font-mono">ANSWER: clue text</span>. Imported entries are stored as seed/admin content (no community attribution) and follow the same validation & duplicate rules.</p>
        <div className="grid grid-cols-4 gap-3 mb-3">
          <div><label className="font-mono-caps text-[9px] text-muted-foreground">Level</label>
            <select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} data-testid="import-zone" className="w-full mt-1 bg-background border border-border px-2 py-2 text-sm"><option value="casual">New Jack</option><option value="golden_era">Triple OG</option><option value="bonus">Bonus</option></select></div>
          <div><label className="font-mono-caps text-[9px] text-muted-foreground">Difficulty</label>
            <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="w-full mt-1 bg-background border border-border px-2 py-2 text-sm"><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option></select></div>
          <div><label className="font-mono-caps text-[9px] text-muted-foreground">Category</label>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mt-1 bg-background border border-border px-2 py-2 text-sm" /></div>
          <div><label className="font-mono-caps text-[9px] text-muted-foreground">Source</label>
            <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full mt-1 bg-background border border-border px-2 py-2 text-sm" /></div>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} data-testid="import-text" placeholder={"NAS: Illmatic emcee from Queensbridge\nBEAT: The instrumental you rhyme over"} className="w-full bg-background border border-border px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-secondary resize-none mb-4" />
        <div className="flex gap-3 mb-4">
          <button onClick={run} disabled={busy || !text.trim()} data-testid="import-run-btn" className="bg-secondary text-secondary-foreground font-mono-caps text-xs px-6 py-3 border border-secondary hover-lift disabled:opacity-40">{busy ? "Validating…" : "Validate & Import"}</button>
          <button onClick={onClose} className="border border-white/20 font-mono-caps text-xs px-5 hover:border-white/40">Close</button>
        </div>
        {result && (
          <div className="space-y-3 text-sm" data-testid="import-result">
            <ResultBlock label="Accepted" color="text-secondary" items={result.accepted.map((a) => `${a.answer} — ${a.clue}`)} />
            <ResultBlock label="Flagged (duplicates)" color="text-amber-500" items={result.flagged.map((f) => `${f.line} · ${f.reason}`)} />
            <ResultBlock label="Rejected" color="text-destructive" items={result.rejected.map((r) => `${r.line} · ${r.reason}`)} />
          </div>
        )}
      </div>
    </div>
  );
}

function ResultBlock({ label, color, items }) {
  if (!items.length) return null;
  return (
    <div>
      <p className={`font-mono-caps text-[10px] ${color} mb-1`}>{label} ({items.length})</p>
      <div className="border border-white/10 bg-background max-h-32 overflow-auto no-scrollbar">
        {items.map((t, i) => <div key={i} className="px-3 py-1.5 border-b border-white/5 text-xs text-muted-foreground">{t}</div>)}
      </div>
    </div>
  );
}

function Daily({ rows, puzzles, reload }) {
  const [zone, setZone] = useState("casual");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [pid, setPid] = useState("");
  const assign = async () => {
    if (!pid) { toast.error("Pick a puzzle"); return; }
    await api.post("/admin/daily", { zone, date, puzzle_id: pid }); toast.success("Daily set"); reload();
  };
  return (
    <div className="space-y-6" data-testid="admin-daily">
      <div className="border border-white/15 bg-card p-6 flex flex-wrap gap-3 items-end">
        <div><label className="font-mono-caps text-[9px] text-muted-foreground">Zone</label>
          <select value={zone} onChange={(e) => setZone(e.target.value)} className="block bg-background border border-border px-3 py-2 text-sm mt-1"><option value="casual">New Jack</option><option value="golden_era">Triple OG</option></select></div>
        <div><label className="font-mono-caps text-[9px] text-muted-foreground">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="block bg-background border border-border px-3 py-2 text-sm mt-1" /></div>
        <div className="flex-1 min-w-[200px]"><label className="font-mono-caps text-[9px] text-muted-foreground">Puzzle</label>
          <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="daily-puzzle-select" className="block w-full bg-background border border-border px-3 py-2 text-sm mt-1">
            <option value="">— select —</option>{(puzzles || []).filter((p) => p.zone === zone && !p.bonus_only).map((p) => <option key={p.puzzle_id} value={p.puzzle_id}>{p.title}</option>)}</select></div>
        <button onClick={assign} data-testid="daily-assign-btn" className="bg-secondary text-secondary-foreground font-mono-caps text-xs px-5 py-2.5 border border-secondary hover-lift">Set Daily</button>
      </div>
      <div className="border border-white/15 bg-card overflow-x-auto no-scrollbar">
        <table className="w-full text-sm"><thead><tr><Th>Date</Th><Th>Zone</Th><Th>Puzzle ID</Th></tr></thead>
          <tbody>{(rows || []).map((d, i) => <tr key={i}><Cell>{d.date}</Cell><Cell className="text-muted-foreground">{d.zone}</Cell><Cell className="font-mono text-xs">{d.puzzle_id}</Cell></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

function Settings({ s, reload }) {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(s ? { ...s } : null); }, [s]);
  if (!form) return <p className="text-muted-foreground text-sm">Loading…</p>;
  const save = async () => {
    await api.put("/admin/settings", {
      zone_access: form.zone_access, premium_price: parseFloat(form.premium_price),
      ads_enabled_for_free: form.ads_enabled_for_free, basic_hint_penalty: parseInt(form.basic_hint_penalty),
      enhanced_hint_penalty: parseInt(form.enhanced_hint_penalty), archive_premium_only: form.archive_premium_only,
    });
    toast.success("Settings saved"); reload();
  };
  const setZa = (z, v) => setForm({ ...form, zone_access: { ...form.zone_access, [z]: v } });
  return (
    <div className="border border-white/15 bg-card p-6 max-w-xl space-y-6" data-testid="admin-settings">
      <div>
        <p className="font-mono-caps text-[10px] text-primary mb-3">Zone Access (monetization)</p>
        {["casual", "golden_era", "new_and_now"].map((z) => (
          <div key={z} className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-sm">{z}</span>
            <select value={form.zone_access[z]} onChange={(e) => setZa(z, e.target.value)} data-testid={`access-${z}`} className="bg-background border border-border px-3 py-1.5 text-sm">
              <option value="free">free</option><option value="premium">premium</option>
            </select>
          </div>
        ))}
      </div>
      <Field label="Premium price ($/mo)" value={form.premium_price} onChange={(v) => setForm({ ...form, premium_price: v })} />
      <Field label="Basic hint penalty" value={form.basic_hint_penalty} onChange={(v) => setForm({ ...form, basic_hint_penalty: v })} />
      <Field label="Enhanced hint penalty" value={form.enhanced_hint_penalty} onChange={(v) => setForm({ ...form, enhanced_hint_penalty: v })} />
      <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={form.ads_enabled_for_free} onChange={(e) => setForm({ ...form, ads_enabled_for_free: e.target.checked })} data-testid="ads-toggle" /> Show ads to free users</label>
      <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={form.archive_premium_only} onChange={(e) => setForm({ ...form, archive_premium_only: e.target.checked })} /> Archive/replays Premium-only</label>
      <button onClick={save} data-testid="save-settings-btn" className="bg-primary text-primary-foreground font-mono-caps text-xs px-6 py-3 border border-primary hover-lift">Save Settings</button>
    </div>
  );
}

const Field = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between">
    <label className="text-sm">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} className="w-28 bg-background border border-border px-3 py-1.5 text-sm text-right" />
  </div>
);
